import React, { useEffect, useState } from 'react';
import { UsersIconFilled, MinusIcon, PlusIcon } from '@rsos/assets/icons';
import ChatAvatar from '@rsos/components/capstone/Chat/ChatAvatar';
import {
  ActionButton,
  ActionsBox,
  AgencyInfo,
  AgencyName,
  AvatarBox,
  chatHeaderIconColor,
} from '@rsos/components/capstone/Chat/ChatHeader/ChatHeader.styles';
import ChatParticipants from '@rsos/components/capstone/Chat/ChatParticipants';
import { Box, Button, Modal, Text } from '@rsos/components/capstone/base';
import {
  ModalBody,
  ModalHeader,
} from '@rsos/components/capstone/base/Modal/ModalComponents';
import Popover from '@rsos/components/capstone/base/Popover';
import {
  Container,
  ContainerSection,
  StatusCircle,
} from './PrivilegedChatHeader.styles';
import { ADMINISTRATIVE_USER_CHAT, getAgencyName } from './helpers';

/**
 * Displays a chat header above the chat box. For Alerts, there are multiple chat participants
 *
 * @param {array} alertChatParticipants - chat participant for alerts
 * @param {boolean} isChatBoxVisible - is the chatbox visible or not
 * @param {boolean} isUnread - are there unread messages in the chat or not
 * @param {Function} onActionButtonClick - callback function to collapse/expand the chat
 */
const PrivilegedChatHeader = ({
  alertChatParticipants,
  isChatBoxVisible,
  isUnread,
  onActionButtonClick = () => {},
  hasChatEnded,
  onEndChat,
  unreadAmount,
}) => {
  const hasEndChatButton = !hasChatEnded && isChatBoxVisible;
  const [isOpened, setIsOpened] = useState(false);
  const [isParticipantsPopoverOpen, setIsParticipantsPopoverOpen] =
    useState(false);
  const [agencyName, setAgencyName] = useState(ADMINISTRATIVE_USER_CHAT);
  const entityDisplayName =
    alertChatParticipants?.[0]?.custom?.entity_display_name;

  const confirmEndChat = () => {
    onEndChat();
    setIsOpened(false);
  };

  useEffect(() => {
    setAgencyName(getAgencyName(hasChatEnded, entityDisplayName));
  }, [entityDisplayName, hasChatEnded]);

  return (
    <Container
      isUnread={isUnread}
      isChatBoxVisible={isChatBoxVisible}
      height={hasEndChatButton ? '100px' : '60px'}
    >
      <ContainerSection>
        {!hasChatEnded && (
          <AvatarBox position="relative">
            <ChatAvatar name={agencyName} />
            {unreadAmount > 0 && (
              <StatusCircle>
                <Text size="xxsmall" color="background">
                  {unreadAmount}
                </Text>
              </StatusCircle>
            )}
          </AvatarBox>
        )}
        <AgencyInfo>
          <AgencyName>{agencyName}</AgencyName>
        </AgencyInfo>
        <ActionsBox>
          {!hasChatEnded && (
            <ActionButton
              onMouseEnter={() => setIsParticipantsPopoverOpen(true)}
              onMouseLeave={() => setIsParticipantsPopoverOpen(false)}
            >
              <Popover
                cssDisplayProperty="block"
                isOpen={isParticipantsPopoverOpen}
                popoverID={`chat-popover`}
                trigger={
                  <UsersIconFilled
                    width={12}
                    height={12}
                    color={chatHeaderIconColor}
                  />
                }
                useCustomOffset
                xOffset={-6}
                yOffset={20}
              >
                <ChatParticipants
                  alertChatParticipants={alertChatParticipants}
                />
              </Popover>
            </ActionButton>
          )}
          <ActionButton noMargin showBorder onClick={onActionButtonClick}>
            {!isChatBoxVisible ? (
              <PlusIcon width={14} height={14} color={chatHeaderIconColor} />
            ) : (
              <MinusIcon width={12} height={12} color={chatHeaderIconColor} />
            )}
          </ActionButton>
        </ActionsBox>
      </ContainerSection>
      {hasEndChatButton && (
        <ContainerSection>
          <Button
            btnType="warning_tertiary"
            grow
            noMargin
            mt="10px"
            onClick={() => setIsOpened(true)}
          >
            End Chat
          </Button>
          <Modal
            id="alert-privileged-end-chat"
            isOpened={isOpened}
            onClose={() => setIsOpened(false)}
            width={350}
            padding={30}
          >
            <ModalHeader onClose={() => setIsOpened(false)}>
              <Text size="header" color="primaryText">
                End Chat?
              </Text>
            </ModalHeader>
            <ModalBody noFooter>
              <Text color="primaryText">
                No additional messages will be able to be sent for this
                incident.
              </Text>
              <Box grow horizontal mt={24}>
                <Button
                  grow
                  btnType="tertiary"
                  ml={0}
                  onClick={() => setIsOpened(false)}
                >
                  Cancel
                </Button>
                <Button
                  grow
                  btnType="warning_primary"
                  mr={0}
                  onClick={confirmEndChat}
                >
                  End Chat
                </Button>
              </Box>
            </ModalBody>
          </Modal>
        </ContainerSection>
      )}
    </Container>
  );
};

export default PrivilegedChatHeader;
